import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Users, Shield, Award, TrendingUp, TrendingDown, UserPlus, UserCheck, Activity, PieChart, CheckCircle, XCircle, Clock } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, BarChart, Bar } from 'recharts';

const getEvolutionBadge = (evolution) => {
  if (evolution > 0) {
    return (
      <Badge className="bg-green-600 text-white border-green-700 font-semibold">
        <TrendingUp className="h-3 w-3 mr-1" />
        +{evolution}%
      </Badge>
    );
  } else if (evolution < 0) {
    return (
      <Badge className="bg-red-600 text-white border-red-700 font-semibold">
        <TrendingDown className="h-3 w-3 mr-1" />
        {evolution}%
      </Badge>
    );
  }
  return (
    <Badge className="bg-gray-600 text-white border-gray-700 font-semibold">
      <Activity className="h-3 w-3 mr-1" />
      Stable
    </Badge>
  );
};

export default function StatistiquesGraphiques({ stats }) {
  if (!stats) return null;
  return (
    <>
      {/* Graphique d'évolution Recharts — Ultra Pro */}
      {stats.serie_evolution && stats.serie_evolution.length > 0 && (
        <Card className="border-2 border-purple-100 shadow-lg overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-slate-900 via-purple-900 to-fuchsia-900 text-white">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
              <div>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Activity className="h-5 w-5 text-fuchsia-300" />
                  Évolution sur la période
                </CardTitle>
                <CardDescription className="text-purple-200 mt-1">
                  Membres, COOPER&apos;S, Experts &amp; Souscriptions — courbe live
                </CardDescription>
              </div>
              <div className="flex items-center gap-2 text-xs text-purple-200">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                {stats.serie_evolution.length} points
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6 bg-gradient-to-br from-white to-purple-50/30">
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={stats.serie_evolution} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gMembres" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.7} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="gCoopers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#a855f7" stopOpacity={0.7} />
                    <stop offset="95%" stopColor="#a855f7" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="gExperts" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f97316" stopOpacity={0.7} />
                    <stop offset="95%" stopColor="#f97316" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="gSouscriptions" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.7} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                <Tooltip
                  contentStyle={{
                    background: 'rgba(15, 23, 42, 0.95)',
                    border: '1px solid rgba(168, 85, 247, 0.4)',
                    borderRadius: 12,
                    color: 'white',
                    boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.3)'
                  }}
                  labelStyle={{ color: '#c4b5fd', fontWeight: 600 }}
                />
                <Legend wrapperStyle={{ paddingTop: 10 }} />
                <Area type="monotone" dataKey="membres" name="Membres" stroke="#3b82f6" strokeWidth={2.5} fill="url(#gMembres)" />
                <Area type="monotone" dataKey="souscriptions" name="Souscriptions" stroke="#10b981" strokeWidth={2.5} fill="url(#gSouscriptions)" />
                <Area type="monotone" dataKey="experts" name="Experts" stroke="#f97316" strokeWidth={2} fill="url(#gExperts)" />
                <Area type="monotone" dataKey="coopers" name="COOPER'S" stroke="#a855f7" strokeWidth={2} fill="url(#gCoopers)" />
              </AreaChart>
            </ResponsiveContainer>

            <div className="mt-4 pt-4 border-t border-purple-100">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                  <Award className="h-4 w-4 text-amber-500" />
                  Revenus quotidiens estimés
                </h4>
                <span className="text-xs text-slate-500">en €</span>
              </div>
              <ResponsiveContainer width="100%" height={120}>
                <BarChart data={stats.serie_evolution} margin={{ top: 5, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gRevenus" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.95} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.4} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#fef3c7" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748b' }} />
                  <Tooltip
                    contentStyle={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(245, 158, 11, 0.4)', borderRadius: 12, color: 'white' }}
                    formatter={(value) => [`${value.toLocaleString('fr-FR')} €`, 'Revenus']}
                  />
                  <Bar dataKey="revenus" fill="url(#gRevenus)" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Statistiques Membres */}
      <Card>
        <CardHeader className="bg-blue-50">
          <CardTitle className="flex items-center gap-2 text-blue-900">
            <Users className="h-5 w-5" />
            Membres Adhérents
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-2 border-blue-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <UserPlus className="h-8 w-8 mx-auto text-blue-600 mb-2" />
                  <p className="text-sm text-slate-600 mb-1">Nouveaux inscrits</p>
                  <p className="text-3xl font-bold text-blue-600">{stats.membres.nouveaux}</p>
                  <div className="mt-2">{getEvolutionBadge(stats.membres.evolution_nouveaux)}</div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-2 border-green-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <CheckCircle className="h-8 w-8 mx-auto text-green-600 mb-2" />
                  <p className="text-sm text-slate-600 mb-1">Validés</p>
                  <p className="text-3xl font-bold text-green-600">{stats.membres.valides}</p>
                  <div className="mt-2">{getEvolutionBadge(stats.membres.evolution_valides)}</div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-2 border-purple-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <UserCheck className="h-8 w-8 mx-auto text-purple-600 mb-2" />
                  <p className="text-sm text-slate-600 mb-1">Assignés</p>
                  <p className="text-3xl font-bold text-purple-600">{stats.membres.assignes}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-2 border-red-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <XCircle className="h-8 w-8 mx-auto text-red-600 mb-2" />
                  <p className="text-sm text-slate-600 mb-1">Rejetés</p>
                  <p className="text-3xl font-bold text-red-600">{stats.membres.rejetes}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Statistiques COOPER'S */}
      <Card>
        <CardHeader className="bg-green-50">
          <CardTitle className="flex items-center gap-2 text-green-900">
            <Shield className="h-5 w-5" />
            COOPER'S
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-2 border-blue-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <UserPlus className="h-8 w-8 mx-auto text-blue-600 mb-2" />
                  <p className="text-sm text-slate-600 mb-1">Nouveaux</p>
                  <p className="text-3xl font-bold text-blue-600">{stats.coopers.nouveaux}</p>
                  <div className="mt-2">{getEvolutionBadge(stats.coopers.evolution_nouveaux)}</div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-2 border-green-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <CheckCircle className="h-8 w-8 mx-auto text-green-600 mb-2" />
                  <p className="text-sm text-slate-600 mb-1">Validés</p>
                  <p className="text-3xl font-bold text-green-600">{stats.coopers.valides}</p>
                  <div className="mt-2">{getEvolutionBadge(stats.coopers.evolution_valides)}</div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-2 border-red-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <XCircle className="h-8 w-8 mx-auto text-red-600 mb-2" />
                  <p className="text-sm text-slate-600 mb-1">Suspendus</p>
                  <p className="text-3xl font-bold text-red-600">{stats.coopers.suspendus}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-2 border-blue-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <Activity className="h-8 w-8 mx-auto text-blue-600 mb-2" />
                  <p className="text-sm text-slate-600 mb-1">Réactivés</p>
                  <p className="text-3xl font-bold text-blue-600">{stats.coopers.reactives}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Statistiques Experts */}
      <Card>
        <CardHeader className="bg-purple-50">
          <CardTitle className="flex items-center gap-2 text-purple-900">
            <Award className="h-5 w-5" />
            Experts
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-2 border-blue-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <UserPlus className="h-8 w-8 mx-auto text-blue-600 mb-2" />
                  <p className="text-sm text-slate-600 mb-1">Nouvelles inscriptions</p>
                  <p className="text-3xl font-bold text-blue-600">{stats.experts.nouveaux}</p>
                  <div className="mt-2">{getEvolutionBadge(stats.experts.evolution_nouveaux)}</div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-2 border-green-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <CheckCircle className="h-8 w-8 mx-auto text-green-600 mb-2" />
                  <p className="text-sm text-slate-600 mb-1">Validés</p>
                  <p className="text-3xl font-bold text-green-600">{stats.experts.valides}</p>
                  <div className="mt-2">{getEvolutionBadge(stats.experts.evolution_valides)}</div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-2 border-orange-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <Clock className="h-8 w-8 mx-auto text-orange-600 mb-2" />
                  <p className="text-sm text-slate-600 mb-1">En attente</p>
                  <p className="text-3xl font-bold text-orange-600">{stats.experts.en_attente}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-2 border-red-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <XCircle className="h-8 w-8 mx-auto text-red-600 mb-2" />
                  <p className="text-sm text-slate-600 mb-1">Rejetés</p>
                  <p className="text-3xl font-bold text-red-600">{stats.experts.rejetes}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Activité Admin */}
      <Card>
        <CardHeader className="bg-slate-50">
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <Activity className="h-5 w-5" />
            Activité Administrative
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-2 border-green-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <CheckCircle className="h-8 w-8 mx-auto text-green-600 mb-2" />
                  <p className="text-sm text-slate-600 mb-1">Validations totales</p>
                  <p className="text-3xl font-bold text-green-600">{stats.activite_admin.validations_totales}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-2 border-red-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <XCircle className="h-8 w-8 mx-auto text-red-600 mb-2" />
                  <p className="text-sm text-slate-600 mb-1">Rejets totaux</p>
                  <p className="text-3xl font-bold text-red-600">{stats.activite_admin.rejets_totaux}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-2 border-purple-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <UserCheck className="h-8 w-8 mx-auto text-purple-600 mb-2" />
                  <p className="text-sm text-slate-600 mb-1">Assignations</p>
                  <p className="text-3xl font-bold text-purple-600">{stats.activite_admin.assignations}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-2 border-blue-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <PieChart className="h-8 w-8 mx-auto text-blue-600 mb-2" />
                  <p className="text-sm text-slate-600 mb-1">Modifications config</p>
                  <p className="text-3xl font-bold text-blue-600">{stats.activite_admin.modifications_config}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Résumé global */}
      <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200">
        <CardContent className="pt-6">
          <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-green-600" />
            Résumé de la période
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg">
              <p className="text-sm text-slate-600 mb-1">Total inscriptions</p>
              <p className="text-2xl font-bold text-blue-600">
                {stats.membres.nouveaux + stats.coopers.nouveaux + stats.experts.nouveaux}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg">
              <p className="text-sm text-slate-600 mb-1">Total validations</p>
              <p className="text-2xl font-bold text-green-600">
                {stats.membres.valides + stats.coopers.valides + stats.experts.valides}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg">
              <p className="text-sm text-slate-600 mb-1">Taux de validation</p>
              <p className="text-2xl font-bold text-purple-600">
                {Math.round(
                  ((stats.membres.valides + stats.coopers.valides + stats.experts.valides) /
                    (stats.membres.nouveaux + stats.coopers.nouveaux + stats.experts.nouveaux)) * 100
                )}%
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
